/**
 * Database Reset Utilities
 *
 * Provides functions to reset local database state
 */

import { logger } from '@/lib/monitoring/logger'

export const RXDB_SESSION_STORAGE_KEY = 'moly-rxdb-initialized'

const clearRxdbSessionFlag = () => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.removeItem(RXDB_SESSION_STORAGE_KEY)
  } catch (error) {
    logger.logDebug('SessionStorage not accessible (likely in private mode)', {
      error: String(error),
    })
  }
}

/**
 * Deletes the IndexedDB database used by PGlite
 * @param dbName - Name of the database to delete (default: 'molydb2')
 * @returns Promise that resolves when database is deleted
 */
export async function deleteLocalDatabase(
  dbName: string = 'molydb2',
): Promise<void> {
  return new Promise((resolve, reject) => {
    const deleteRequest = indexedDB.deleteDatabase(dbName)

    deleteRequest.onsuccess = () => {
      resolve()
    }

    deleteRequest.onerror = () => {
      reject(deleteRequest.error)
    }

    deleteRequest.onblocked = () => {
      reject(new Error('Database deletion blocked - close all tabs'))
    }
  })
}

/**
 * Deletes all RxDB databases from IndexedDB
 * RxDB databases are prefixed with 'rxdb-dexie-'
 * @returns Promise that resolves when all RxDB databases are deleted
 */
export async function deleteAllRxDBDatabases(): Promise<void> {
  const databases = await indexedDB.databases()
  const rxdbDatabases = databases
    .filter((db) => db.name?.startsWith('rxdb-dexie-') || db.name?.includes('moly'))
    .map((db) => db.name!)

  if (rxdbDatabases.length === 0) {
    return
  }

  const deletePromises = rxdbDatabases.map((dbName) => {
    return new Promise<void>((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase(dbName)

      deleteRequest.onsuccess = () => {
        resolve()
      }

      deleteRequest.onerror = () => {
        reject(deleteRequest.error)
      }

      deleteRequest.onblocked = () => {
        reject(new Error(`Database deletion blocked: ${dbName}`))
      }
    })
  })

  await Promise.all(deletePromises)
}

/**
 * Complete reset of database, sync, and auth state
 * WARNING: This will delete all local data
 */
export function resetAllLocalData(): void {
  // Set flag to delete database on next load (before database opens it)
  localStorage.setItem('moly_reset_pending', 'true')
  localStorage.setItem('moly_reset_rxdb_pending', 'true')

  // Force splash screen to appear again post-reset
  clearRxdbSessionFlag()

  // Reload immediately (this closes all database connections)
  window.location.reload()
}

/**
 * Reset only RxDB databases without affecting other data
 * WARNING: This will delete all local RxDB data
 */
export function resetRxDB(): void {
  // Set flag to delete RxDB databases on next load
  localStorage.setItem('moly_reset_rxdb_pending', 'true')

  // Force splash screen to appear again post-reset
  clearRxdbSessionFlag()

  // Reload to close connections and delete databases
  window.location.reload()
}

/**
 * Checks and handles pending reset operations
 * This should be called BEFORE initializing databases
 * @returns Promise that resolves when reset operations are complete
 */
export async function handlePendingResets(): Promise<void> {
  // One-shot cleanup of the legacy un-prefixed purge metadata key. Before
  // per-workspace scoping, PurgeService wrote a single global
  // `rxdb_purge_metadata` key that would bleed across workspaces. Remove it
  // on boot so it doesn't accumulate; new writes always use the
  // rxdb_purge_metadata_<workspaceId> form.
  try {
    localStorage.removeItem('rxdb_purge_metadata')
  } catch {
    // ignore — localStorage may be unavailable in private mode
  }

  const resetPending = localStorage.getItem('moly_reset_pending')
  const rxdbResetPending = localStorage.getItem('moly_reset_rxdb_pending')

  if (resetPending === 'true') {
    localStorage.removeItem('moly_reset_pending')

    try {
      await deleteLocalDatabase('molydb2')
    } catch (error) {
      logger.logDebug('Failed to delete local database, proceeding anyway', {
        dbName: 'molydb2',
        error: String(error),
      })
    }
  }

  if (rxdbResetPending === 'true') {
    localStorage.removeItem('moly_reset_rxdb_pending')

    try {
      await deleteAllRxDBDatabases()
    } catch (error) {
      logger.logDebug('Failed to delete RxDB databases, proceeding anyway', {
        error: String(error),
      })
    }
  }
}
